import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { useLanguage, useLanguageFromUrl } from "@/context/LanguageContext";

const INSTAGRAM_URL = "https://www.instagram.com/beauty_by_kuleva";
const FACEBOOK_URL = "https://www.facebook.com/share/1BvBZyGQ9s/?mibextid=wwXIfr";
const TIMMA_URL = "https://bestill.timma.no/beautybykuleva";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_beauty-oasis-20/artifacts/8w9qtp5u_logo1.jpg";

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, t } = useLanguage();
  const { switchLanguage } = useLanguageFromUrl();

  // Get the base path for current language
  const langPrefix = `/${language}`;

  const navLinks = [
    { name: t("nav.home"), path: langPrefix },
    { name: t("nav.services"), path: `${langPrefix}/services` },
    { name: t("nav.wedding"), path: `${langPrefix}/wedding` },
    { name: t("nav.gallery"), path: `${langPrefix}/gallery` },
    { name: t("nav.contact"), path: `${langPrefix}/contact` },
  ];

  const isActive = (path) => {
    if (path === langPrefix) {
      return location.pathname === path || location.pathname === `${path}/`;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 nav-glass border-b border-[#E8D0D0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to={langPrefix}
            className="flex items-center"
            data-testid="navbar-logo"
          >
            <img 
              src={LOGO_URL}
              alt="Beauty by Kuleva"
              className="w-auto"
              style={{ height: "64px" }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`relative font-medium text-sm tracking-wide transition-colors hover:text-[#D4A5A5] ${
                  isActive(link.path) ? "text-[#D4A5A5]" : "text-[#4A4A4A]"
                }`}
              >
                {link.name}
                {isActive(link.path) && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-[#D4A5A5] rounded-full"
                  />
                )}
              </Link>
            ))}
          </nav>

          {/* Right Side - Actions */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Social Icons */}
            <div className="flex items-center space-x-2">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-[#F9F5F6] flex items-center justify-center text-[#D4A5A5] hover:bg-[#D4A5A5] hover:text-white transition-colors"
                aria-label="Instagram"
                data-testid="navbar-instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-[#F9F5F6] flex items-center justify-center text-[#D4A5A5] hover:bg-[#D4A5A5] hover:text-white transition-colors"
                aria-label="Facebook"
                data-testid="navbar-facebook"
              >
                <Facebook size={16} />
              </a>
            </div>

            {/* Language Switcher */}
            <div className="flex items-center border border-[#E8D0D0] rounded-full overflow-hidden" data-testid="language-switcher">
              <button
                onClick={() => switchLanguage("no")}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  language === "no"
                    ? "bg-[#D4A5A5] text-white"
                    : "text-[#4A4A4A] hover:bg-[#F9F5F6]"
                }`}
                data-testid="lang-no-btn"
              >
                NO
              </button>
              <button
                onClick={() => switchLanguage("en")}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                  language === "en"
                    ? "bg-[#D4A5A5] text-white"
                    : "text-[#4A4A4A] hover:bg-[#F9F5F6]"
                }`}
                data-testid="lang-en-btn"
              >
                EN
              </button>
            </div>

            {/* Book Button */}
            <a
              href={TIMMA_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm"
              data-testid="navbar-book-btn"
            >
              {t("nav.bookNow")}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-[#F9F5F6]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? (
              <X size={24} className="text-[#4A4A4A]" />
            ) : (
              <Menu size={24} className="text-[#4A4A4A]" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mobile-menu border-t border-[#E8D0D0]"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-2 text-lg font-medium transition-colors ${
                    isActive(link.path) ? "text-[#D4A5A5]" : "text-[#4A4A4A]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Language Switcher */}
              <div className="flex items-center gap-2 py-2">
                <span className="text-[#8C8C8C] text-sm">{language === "no" ? "Språk:" : "Language:"}</span>
                <div className="flex items-center border border-[#E8D0D0] rounded-full overflow-hidden">
                  <button
                    onClick={() => {
                      switchLanguage("no");
                      setMobileMenuOpen(false);
                    }}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      language === "no"
                        ? "bg-[#D4A5A5] text-white"
                        : "text-[#4A4A4A]"
                    }`}
                  >
                    NO
                  </button>
                  <button
                    onClick={() => {
                      switchLanguage("en");
                      setMobileMenuOpen(false);
                    }}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      language === "en"
                        ? "bg-[#D4A5A5] text-white"
                        : "text-[#4A4A4A]"
                    }`}
                  >
                    EN
                  </button>
                </div>
              </div>
              
              {/* Mobile Social & Book */}
              <div className="pt-4 border-t border-[#E8D0D0] space-y-4">
                <div className="flex items-center space-x-4">
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#F9F5F6] flex items-center justify-center text-[#D4A5A5]"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#F9F5F6] flex items-center justify-center text-[#D4A5A5]"
                    aria-label="Facebook"
                  >
                    <Facebook size={20} />
                  </a>
                </div>
                <a
                  href={TIMMA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full text-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("nav.bookNow")}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
